'use client';

import { isValidElement, ReactNode, useEffect, useRef, useState } from 'react';

import {
  Ban,
  CheckCircle2,
  Circle,
  CircleQuestionMark,
  KeyRound,
  Loader2,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import { useLatest } from 'react-use';

import type { GuestWait, ProgressStepStatus } from '@/components/assistant-ui/thread/trail-records';
import { CHIP_COLORS } from '@/components/ui/status-chip';
import { cn } from '@/lib/utils';

const GUEST_WAIT_GLYPHS: Readonly<Record<GuestWait, LucideIcon>> = {
  signIn: KeyRound,
  confirm: CircleQuestionMark,
};

function StatusGlyph({
  status,
  customInProgressIcon,
  waitsFor = 'confirm',
}: {
  status: ProgressStepStatus;
  customInProgressIcon?: ReactNode;
  waitsFor?: GuestWait;
}) {
  if (status === 'completed') {
    return <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />;
  }
  if (status === 'failed') {
    return <XCircle className="h-5 w-5 text-destructive" />;
  }
  if (status === 'cancelled') {
    // Ban (matches the card's "cancelled" alert), gray — a neutral stop, not an error.
    return <Ban className="h-5 w-5 text-muted-foreground" />;
  }
  if (status === 'awaiting-approval') {
    // The guest's turn, not work: a still glyph in the attention colour, never the step's own
    // rotating icons, so it cannot read as the agent still working.
    const Glyph = GUEST_WAIT_GLYPHS[waitsFor];
    return <Glyph className={cn('h-5 w-5', CHIP_COLORS.amber.text)} aria-hidden="true" />;
  }
  if (status === 'in-progress') {
    if (customInProgressIcon) return <>{customInProgressIcon}</>;
    return (
      <Loader2
        className="h-5 w-5 animate-spin text-(--trail-icon-color,var(--primary)) motion-reduce:animate-none"
        aria-hidden="true"
      />
    );
  }
  return <Circle className="h-5 w-5 text-muted-foreground" />;
}

export const STATUS_TRANSITION_MS = 300;

/**
 * A step's status glyph and label. The glyph crossfades when `status`, `iconKey` (a row morph at
 * the same React key), the in-progress icon node's `key` or a waiting step's wait changes:
 * fade-and-shrink, swap, fade-and-grow. The label always swaps at once, as tool steps do.
 *
 * `customInProgressIcon` is a fresh ReactNode each render, so comparing it
 * would re-fire the effect every tick; we read the *latest* node from a ref
 * inside the swap timer, and gate the transition on `status` + `iconKey` +
 * the node's `key` (all stable strings). Concretely: a tick that only
 * re-renders the icon node (same key) won't restart the crossfade.
 */
export function TrailStepStatus({
  status,
  customInProgressIcon,
  iconKey,
  waitsFor,
  label,
  announcement,
}: {
  status: ProgressStepStatus;
  customInProgressIcon?: ReactNode;
  iconKey?: string;
  waitsFor?: GuestWait;
  label: string;
  /** Screen-reader text read between the glyph and the label. */
  announcement?: ReactNode;
}) {
  const iconNodeKey = isValidElement(customInProgressIcon) ? customInProgressIcon.key : null;
  // Only a waiting glyph shows what it waits for; the outgoing glyph keeps its own while it fades.
  const glyphWait = status === 'awaiting-approval' ? waitsFor : undefined;
  const [visible, setVisible] = useState(() => ({
    status,
    iconKey,
    iconNodeKey,
    glyphWait,
    customInProgressIcon,
  }));
  const [isTransitioning, setIsTransitioning] = useState(false);
  const swapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Read the latest icon node lazily when the timer fires so a fresh
  // ReactNode on every render doesn't force the effect to re-run; the
  // visible state still holds the OLD icon during the fade-out phase.
  const latestRef = useLatest(customInProgressIcon);
  const statusChanged =
    status !== visible.status ||
    iconKey !== visible.iconKey ||
    iconNodeKey !== visible.iconNodeKey ||
    glyphWait !== visible.glyphWait;

  useEffect(() => {
    // Back to what is shown before the swap: nothing left to fade in, and the cleanup below has
    // already dropped the timer that would have ended the fade.
    if (!statusChanged) {
      setIsTransitioning(false);
      return;
    }
    // setState-in-effect is intentional: the CSS crossfade is driven by an
    // `isTransitioning` class flip on the wrapper, then a delayed `visible`
    // swap. There's no external system to subscribe to — the animation
    // timing IS the state machine.

    setIsTransitioning(true);
    if (swapTimerRef.current) clearTimeout(swapTimerRef.current);
    swapTimerRef.current = setTimeout(() => {
      setVisible({
        status,
        iconKey,
        iconNodeKey,
        glyphWait,
        customInProgressIcon: latestRef.current,
      });
      setIsTransitioning(false);
      swapTimerRef.current = null;
    }, STATUS_TRANSITION_MS);
    return () => {
      if (swapTimerRef.current) {
        clearTimeout(swapTimerRef.current);
        swapTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- latestRef is a stable ref from useLatest
  }, [statusChanged, status, iconKey, iconNodeKey, glyphWait]);

  // Reduced motion swaps the glyph at the end of the hold instead of fading it out and in.
  const fadedOut = isTransitioning && 'motion-safe:scale-50 motion-safe:opacity-0';
  return (
    <>
      <span className="flex h-5 w-5 shrink-0 items-center justify-center" aria-hidden="true">
        <span
          className={cn(
            'inline-flex scale-100 opacity-100 transition-all duration-300 ease-in-out motion-reduce:transition-none',
            fadedOut,
          )}
        >
          <StatusGlyph
            status={visible.status}
            customInProgressIcon={visible.customInProgressIcon}
            waitsFor={visible.glyphWait}
          />
        </span>
      </span>
      {announcement}
      <span
        className={cn(
          'min-w-0 flex-1 text-sm leading-5 text-foreground',
          status === 'pending' && 'text-muted-foreground',
          status === 'in-progress' && 'shimmer shimmer-speed-150',
        )}
      >
        {label}
      </span>
    </>
  );
}
